import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TagEditor from '../Widgets/TagEditor';
import StorageMentionPicker from '../Common/StorageMentionPicker';

const ProjectChatComposer = forwardRef(({ text, files, onText, onFiles, onSubmit,
  onPendingChange, disabled, active = true, caseid, tags = [], hasMoreTags, onMoreTags }, ref) => {
  const { t } = useTranslation();
  const editor = useRef(null);
  const picker = useRef(null);
  const mentionTrigger = useRef(false);
  const [uploading, setUploading] = useState(false);
  const [failedFiles, setFailedFiles] = useState([]);
  useEffect(() => onPendingChange(uploading || failedFiles.length > 0), [uploading, failedFiles, onPendingChange]);
  useEffect(() => () => onPendingChange(false), [onPendingChange]);
  useImperativeHandle(ref, () => ({
    focus: () => editor.current?.focus(),
    getReferences: () => ({ files: files.map(({ path }) => ({ path })),
      tags: editor.current?.getTagReferences() || [] }),
    hasPendingAttachments: () => uploading || failedFiles.length > 0 || editor.current?.hasPendingAttachments(),
  }), [files, uploading, failedFiles]);
  const pending = value => { setUploading(value); if (value) setFailedFiles([]); };
  const select = (reference, options = {}) => {
    editor.current?.insertStorageMention(reference, {
      replaceMentionTrigger: options.replaceMentionTrigger ?? mentionTrigger.current,
      preserveText: options.preserveText,
    });
    onFiles(current => [...new Map([...current, reference].map(file => [file.path, file])).values()].slice(0, 8));
    mentionTrigger.current = false;
  };
  const remove = path => {
    editor.current?.removeStorageMention(path);
    onFiles(current => current.filter(file => file.path !== path));
  };
  return <Box sx={{ flex: 1, minWidth: 0 }}>
    <TagEditor ref={editor} value={text} onChange={onText} tags={tags}
      caseid={caseid} includeDashboardTags={false} includeSystemTags={false} referenceOnly
      aria-label={t('project_chat.message')} placeholder={t('project_chat.message')}
      storageReferences={files} disabled={disabled} active={active} minRows={2} maxRows={5} dense
      onKeyDown={event => {
        if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && event.keyCode !== 229) {
          event.preventDefault();
          if (!uploading) onSubmit();
        }
      }}
      onStorageMentionTrigger={() => { mentionTrigger.current = true; picker.current?.open(); }}
      onStorageMentionRemove={path => onFiles(current => current.filter(file => file.path !== path))}
      onStorageFilesDrop={incoming => picker.current?.attachFiles(incoming)} />
    <StorageMentionPicker ref={picker} selectedRefs={files} onSelect={select} onRemove={remove}
      disabled={disabled} active={active} compact showTriggerButton={false} maxReferences={8} onPendingChange={pending}
      onClose={() => { mentionTrigger.current = false; if (active && !disabled) editor.current?.focus(); }}
      onUploadFailure={setFailedFiles} />
    {failedFiles.length > 0 && <Box>
      <Button size="small" disabled={disabled || uploading} onClick={() => picker.current?.attachFiles(failedFiles)}>
        {t('project_chat.references.retry_upload')}
      </Button>
      <Button size="small" disabled={disabled || uploading} onClick={() => {
        setFailedFiles([]);
        picker.current?.clearError();
      }}>
        {t('project_chat.references.remove_upload')}
      </Button>
    </Box>}
    <Box component="details" sx={{ color: 'text.secondary' }}>
      <Typography component="summary" variant="caption" sx={{ cursor: 'pointer' }}>{t('project_chat.references.help')}</Typography>
      <Typography variant="caption">{t('project_chat.references.hint')}</Typography>
    </Box>
    {hasMoreTags && <Button size="small" onClick={onMoreTags} disabled={disabled}>
      {t('project_chat.references.more_tags')}
    </Button>}
  </Box>;
});

export default ProjectChatComposer;
